/**
 * Creates a debounced function that postpones invoking `callback` until
 * after `wait` milliseconds have elapsed since the last invocation.
 *
 * @param {Function} callback - The function to debounce.
 * @param {number} [wait=300] - The debounce delay in milliseconds.
 * @returns {((...args: any[]) => void) & { cancel: () => void }}
 * A debounced function with a `cancel` method to clear a pending invocation.
 */
export default function debounce(callback, wait = 300) {
  let timeoutId = null;

  /**
   * Schedules the callback execution and resets any pending timer.
   *
   * @param {...any} args - Arguments passed through to the callback.
   * @returns {void}
   */
  function debounced(...args) {
    const context = this;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      timeoutId = null;
      callback.apply(context, args);
    }, wait);
  }

  /**
   * Cancels the currently scheduled callback execution, if any.
   *
   * @returns {void}
   */
  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}
